import React, { useRef, useState } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { Formik, useFormikContext } from 'formik';
import { isEmpty, isNil, uniq, uniqBy } from 'lodash';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import { getContactPrimaryEmail } from '~/components/Contacts/ContactUtils';
import AlertBanner from '~/components/core/AlertBanner';
import ArrayMultiselectFieldWithChipsFormik from '~/components/core/ArrayMultiselectFieldWithChipsFormik';
import Button from '~/components/core/Atomic/Buttons/Button';
import ButtonsContainer from '~/components/core/Atomic/Buttons/ButtonsContainer';
import CancelButton from '~/components/core/Buttons/CancelButton';
import useFormikChangeListener from '~/components/core/Formik/FormikChangeListener';
import LobMultiSelectWithChipsFormik, {
  concreteSelectedLobs,
  isAllLobsSelected,
} from '~/components/core/Formik/LobMultiSelectWithChipsFormik';
import SubOrgMultiSelectWithChipsFormik, {
  isAllSubOrganizationsSelected,
} from '~/components/core/Formik/SubOrgMultiSelectWithChipsFormik';
import Heading from '~/components/core/TextComponents/Heading';
import useOrganization from '~/components/OrganizationContext';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { SysconfigOrganization } from '~/components/SystemConfiguration/SystenConfigurationTypes';
import TextFieldFormik from '~/components/TextFieldFormik';
import type {
  MoiMethodInfoListMoiMethodInfo,
  MoiVendorAssignmentRuleCreateParams,
  MoiVendorAssignmentRuleInfo,
} from '~/server_shared/openapi/data-contracts';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';
import cn from '~/Utils/cn';

import { useOrganizationMoiMethods } from './hooks/useOrganizationMoiMethods';
import MoiAssignmentConditionsQueryBuilderFormik from './QueryBuilder/MoiAssignmentConditionsQueryBuilderFormik';
import type { MoiAssigneeStrategy, MoiAssignmentRuleFormValues } from './formValues';
import { FIELD_IDS } from './formValues';
import MoiAssigneeFieldsFormik from './MoiAssigneeFieldsFormik';
import {
  ALL_MOI_METHODS_OPTION,
  fromMoiMethodScope,
  isAllMoiMethodsSelected,
  toMoiMethodScope,
} from './moiMethodsScope';
import { TEXTS } from './texts';

const ASSIGNEE_STRATEGIES: MoiAssigneeStrategy[] = ['static', 'contact_role'];

/** The backend's `vendor_contact_problem`, on the contact the picker returns: an MOI vendor must be
 *  active, an MOI service provider, and reachable by email, which is how the vendor is notified.
 *  `getContactPrimaryEmail` picks the address the way the backend's `primary_email` does — the first
 *  one neither removed nor blank. */
const isEligibleMoiVendorContact = (contact: ContactFullModel): boolean =>
  !contact.is_deactivated && !!contact.is_moi_service_provider && !!getContactPrimaryEmail(contact);

/** The methods a rule with these values covers, as the backend's `_covers_method` reads them: active,
 *  in the rule's method and LOB scope, and org-level or offered to a sub-organization the rule covers. */
const getCoveredMoiMethods = (
  methods: MoiMethodInfoListMoiMethodInfo[],
  values: MoiAssignmentRuleFormValues,
  subOrganizationEnabled: boolean
): MoiMethodInfoListMoiMethodInfo[] => {
  const isAllMethods = isAllMoiMethodsSelected(values.moi_methods);
  const isAllLobs = values.is_all_lobs || isAllLobsSelected(values.lobs);
  const lobs = concreteSelectedLobs(values.lobs);
  // A flat organization submits every sub-organization, as `onSubmit` below does.
  const isAllSubOrgs = !subOrganizationEnabled || isAllSubOrganizationsSelected(values.sub_organization_ids);
  return methods.filter(
    (method) =>
      method.is_active &&
      (isAllMethods || values.moi_methods.includes(method.key)) &&
      (isAllLobs || lobs.includes(method.lob)) &&
      (method.is_org_level ||
        (isAllSubOrgs
          ? !isEmpty(method.sub_organization_ids)
          : method.sub_organization_ids.some((id) => values.sub_organization_ids.includes(id))))
  );
};

/** `methods` is `null` while the organization's methods are unknown — still loading, or refused —
 *  and then no stored key can be told stale, nor a contact's expertise checked. */
const createValidationSchema = (subOrganizationEnabled: boolean, methods: MoiMethodInfoListMoiMethodInfo[] | null) => {
  const methodKeys = methods?.map((method) => method.key) ?? null;
  // `is_all=false` with no values is rejected by the engine, which refuses a rule scoped to nothing.
  const atLeastOneMethod = Yup.array().min(1, TEXTS.MOI_METHODS_REQUIRED);

  return Yup.object().shape({
    [FIELD_IDS.NAME]: Yup.string().required(TEXTS.NAME_REQUIRED),
    // The BE rejects a rule that resolves to no sub-organization or no LOB, so the dialog must not
    // be able to produce that state. A flat organization has no rows to pick, hence no rule here.
    [FIELD_IDS.SUB_ORGANIZATION_IDS]: subOrganizationEnabled
      ? Yup.array().min(1, TEXTS.SUB_ORGANIZATIONS_REQUIRED)
      : Yup.array(),
    [FIELD_IDS.LOBS]: Yup.array().when(FIELD_IDS.ALL_LOBS, {
      is: false,
      then: (schema) => schema.min(1, TEXTS.LOBS_REQUIRED),
      otherwise: (schema) => schema,
    }),
    // Membership is checked as well as count. The BE stores whatever keys a rule is saved with — it
    // does not check them against the organization's methods — and `ArrayMultiselectFieldWithChipsFormik`
    // keeps a selected key that no option carries, so a rule naming a method the organization no
    // longer has would otherwise be saved straight back, scoped to a method no inspection can have.
    [FIELD_IDS.MOI_METHODS]: methodKeys
      ? atLeastOneMethod.of(Yup.string().oneOf([ALL_MOI_METHODS_OPTION.id, ...methodKeys], TEXTS.MOI_METHOD_STALE))
      : atLeastOneMethod,
    [FIELD_IDS.ASSIGNEE_STRATEGY]: Yup.string().oneOf(ASSIGNEE_STRATEGIES),
    [FIELD_IDS.CONTACT_ID]: Yup.mixed().when(FIELD_IDS.ASSIGNEE_STRATEGY, {
      is: 'static',
      then: (schema) => schema.required(TEXTS.CONTACT_REQUIRED),
    }),
    [FIELD_IDS.CONTACT]: Yup.mixed<ContactFullModel>()
      .nullable()
      .test('eligible', TEXTS.CONTACT_INELIGIBLE, (contact) => isNil(contact) || isEligibleMoiVendorContact(contact))
      // The backend refuses a specific contact without an expertise of every method the rule covers,
      // as inspection creation would refuse it as the vendor.
      .test('expertise', (contact, context) => {
        if (isNil(contact) || isNil(methods)) {
          return true;
        }
        const expertises = contact.moi_expertises ?? [];
        const uncovered = getCoveredMoiMethods(methods, context.parent, subOrganizationEnabled).filter(
          (method) => !method.moi_expertises.some((expertise) => expertises.includes(expertise))
        );
        return (
          isEmpty(uncovered) ||
          context.createError({
            message: TEXTS.CONTACT_LACKS_EXPERTISE(uniq(uncovered.map((method) => method.display_name))),
          })
        );
      }),
    [FIELD_IDS.CONTACT_ROLE]: Yup.string().when(FIELD_IDS.ASSIGNEE_STRATEGY, {
      is: 'contact_role',
      then: (schema) => schema.required(TEXTS.ROLE_REQUIRED),
    }),
    // No conditions_json rule: an empty tree is valid and means the rule matches every inspection
    // in scope, the same semantic as the sibling engines.
  });
};

/** A scope's identity, independent of which representation the fields currently hold it in.
 *  Reads only its argument, so it lives at module scope rather than being rebuilt per render. */
const scopeSignature = (vals: MoiAssignmentRuleFormValues): string => {
  const lobsPart =
    vals.is_all_lobs || isAllLobsSelected(vals.lobs) ? 'all' : [...vals.lobs].map(String).sort().join(',');
  // No `subOrganizationEnabled` special-case: a flat organization never writes this field, so
  // including it unconditionally costs nothing and keeps the sub-org dimension load-bearing.
  const subOrgPart = isAllSubOrganizationsSelected(vals.sub_organization_ids)
    ? 'all'
    : [...vals.sub_organization_ids].sort((a, b) => a - b).join(',');
  return `${lobsPart}|${subOrgPart}`;
};

/** One option per method key: a rule scopes by key alone, while the organization's methods are
 *  configured per LOB, so a key offered on several LOBs comes back once for each. A new array on
 *  every call, which the field needs: it prepends `allOption` to the array it is handed, in place. */
const toMoiMethodOptions = (methods: MoiMethodInfoListMoiMethodInfo[]) =>
  uniqBy(methods, (method) => method.key).map((method) => ({
    id: method.key,
    label: method.display_name,
    value: method.key,
  }));

interface AddOrEditMoiAssignmentRuleDialogProps {
  onSubmit: (values: MoiVendorAssignmentRuleCreateParams) => Promise<void>;
  onCancel: () => void;
  rule?: MoiVendorAssignmentRuleInfo | null;
  /** The static contact's name, which the tab resolves; seeds `contact_full_name` on edit. */
  contactFullName?: string;
}

const AddOrEditMoiAssignmentRuleDialog: React.FC<AddOrEditMoiAssignmentRuleDialogProps> = ({
  onSubmit,
  onCancel,
  rule,
  contactFullName,
}) => {
  // `useSysconfig` and `useOrganization` are exported from JSX files without types; cast at the boundary.
  const { organization } = useSysconfig() as { organization: SysconfigOrganization };
  const { subOrganizationEnabled } = useOrganization() as { subOrganizationEnabled: boolean };
  // Read here rather than in the form below, which shows the same list: this Formik holds the
  // validation schema the list feeds.
  const { methods, isLoading: isLoadingMethods, isError: isMethodsError } = useOrganizationMoiMethods(organization.id);
  // A failed read leaves the keys and the contact's expertise unchecked rather than every stored key
  // reading as stale. It fails
  // for an operator who may edit rules but not read claim configuration, which the methods route
  // asks for and the rules routes do not.
  const knownMethods = isLoadingMethods || isMethodsError ? null : methods;
  const assignee = rule?.assignee;

  return (
    <Formik<MoiAssignmentRuleFormValues>
      initialValues={{
        [FIELD_IDS.ID]: rule?.id ?? null,
        [FIELD_IDS.NAME]: rule?.name ?? '',
        [FIELD_IDS.SUB_ORGANIZATION_IDS]: !subOrganizationEnabled
          ? []
          : rule?.scope?.is_all_sub_orgs
            ? [SubOrgMultiSelectWithChipsFormik.ALL_OPTION.id]
            : (rule?.scope?.sub_organization_ids ?? []),
        [FIELD_IDS.LOBS]: rule?.scope?.lobs ?? [],
        [FIELD_IDS.ALL_LOBS]: rule?.scope?.is_all_lobs ?? false,
        [FIELD_IDS.MOI_METHODS]: fromMoiMethodScope(rule?.moi_methods),
        [FIELD_IDS.ASSIGNEE_STRATEGY]: assignee?.strategy ?? 'static',
        [FIELD_IDS.CONTACT_ID]: assignee?.strategy === 'static' ? assignee.contact_id : '',
        [FIELD_IDS.CONTACT_FULL_NAME]: contactFullName ?? '',
        [FIELD_IDS.CONTACT]: null,
        [FIELD_IDS.CONTACT_ROLE]: assignee?.strategy === 'contact_role' ? assignee.contact_role : '',
        [FIELD_IDS.IS_ENABLED]: rule?.is_enabled ?? true,
        [FIELD_IDS.CONDITIONS_JSON]: (rule?.conditions as RuleGroupType | null) ?? null,
      }}
      validationSchema={createValidationSchema(subOrganizationEnabled, knownMethods)}
      onSubmit={async (values) => {
        try {
          // A flat organization has no sub-organization rows at all, so "every sub-organization" is
          // the only resolvable scope — the BE's validator rejects an empty id list.
          const isAllSubOrgs = subOrganizationEnabled
            ? isAllSubOrganizationsSelected(values.sub_organization_ids)
            : true;
          // Either representation counts as "every LOB": the flag the server stores, or the
          // sentinel the field keeps in `lobs`. Reading both, and filtering the sentinel out of
          // the values list, means no transient combination of the two can post `lobs: [0]`
          // against a `list[str]`.
          const isAllLobs = values.is_all_lobs || isAllLobsSelected(values.lobs);

          const submitValues: MoiVendorAssignmentRuleCreateParams = {
            name: values.name,
            // The picked strategy's own field alone: each assignee member forbids the other's.
            assignee:
              values.assignee_strategy === 'static'
                ? { strategy: 'static', contact_id: Number(values.contact_id) }
                : { strategy: 'contact_role', contact_role: values.contact_role },
            moi_methods: toMoiMethodScope(values.moi_methods),
            scope: {
              is_all_sub_orgs: isAllSubOrgs,
              sub_organization_ids: isAllSubOrgs ? [] : values.sub_organization_ids,
              is_all_lobs: isAllLobs,
              lobs: isAllLobs ? [] : concreteSelectedLobs(values.lobs),
            },
            conditions: values.conditions_json ?? null,
            is_enabled: values.is_enabled,
          };
          await onSubmit(submitValues);
          onCancel();
        } catch {
          // Swallowed rather than rethrown, and with no `setSubmitting(false)`: the api layer
          // already surfaced the failure via `reportAxiosError`, Formik clears the flag itself once
          // the returned promise settles, and letting the rejection escape makes Formik's own
          // `handleSubmit` log "An unhandled error was caught from submitForm()" over the toast.
        }
      }}
    >
      <AddOrEditMoiAssignmentRuleDialogInner
        onCancel={onCancel}
        methods={methods}
        isLoadingMethods={isLoadingMethods}
      />
    </Formik>
  );
};

interface AddOrEditMoiAssignmentRuleDialogInnerProps {
  onCancel: () => void;
  /** The organization's MOI methods, empty while they load and after a failed load. */
  methods: MoiMethodInfoListMoiMethodInfo[];
  isLoadingMethods: boolean;
}

const AddOrEditMoiAssignmentRuleDialogInner: React.FC<AddOrEditMoiAssignmentRuleDialogInnerProps> = ({
  onCancel,
  methods,
  isLoadingMethods,
}) => {
  const { organization } = useSysconfig() as { organization: SysconfigOrganization };
  // `operational_details` builds the field as `sub_organizations_enabled and [...]`, so an
  // organization without sub-organizations gets the boolean `false` rather than an empty list.
  // `?? []` does not catch that (`??` only replaces null/undefined), so normalise here — this is
  // the single read the array consumer below is fed from.
  const { subOrganizationEnabled, subOrganizations: rawSubOrganizations } = useOrganization() as {
    subOrganizationEnabled: boolean;
    subOrganizations: { id: number; name?: string }[] | false;
  };
  const subOrganizations = Array.isArray(rawSubOrganizations) ? rawSubOrganizations : [];
  const { isSubmitting, handleSubmit, values, setFieldValue } = useFormikContext<MoiAssignmentRuleFormValues>();
  const [isLoadingConditions, setIsLoadingConditions] = useState(false);
  const { classes } = useStyles();

  const isDisabled = isSubmitting || isLoadingConditions;
  const isEditing = values.id !== null;
  const subOrganizationIds = values.sub_organization_ids;
  // "All (inc. future lobs)" is stored as the sentinel id 0, not as a LOB key, so it must not reach
  // the catalog as a LOB named "0". Read off the forwarded value rather than `is_all_lobs`: that flag
  // is set by a change listener one render later, and the intervening render would fire the fetch.
  // The same derivation feeds the `isAllLobs` flag the catalog request carries.
  const isAllLobs = isAllLobsSelected(values.lobs);
  const catalogLobs = isAllLobs ? [] : concreteSelectedLobs(values.lobs);

  // Clear the conditions tree whenever a scope dimension changes: the catalog refetches on these
  // keys (LOB and sub-org narrow which providers contribute), so a tree authored under the old
  // scope must not silently carry over.
  //
  // Keyed on a *normalised* signature rather than on the raw fields, because not every write to
  // them is a user edit: `LobMultiSelectWithChipsFormik` rewrites both LOB fields during mount to
  // move an all-LOBs rule from the server's representation (`is_all_lobs` with an empty `lobs`)
  // into its own (the All sentinel inside `lobs`). Those writes describe the same scope, so they
  // must compare equal here — otherwise merely opening such a rule nulls its conditions and the
  // next Update posts that null as an explicit clear.
  // Lazily initialised: `useRef(x)` evaluates `x` on every render and keeps only the first, so
  // seeding it inline would recompute the signature for nothing on each pass.
  const lastScopeRef = useRef<string | null>(null);
  if (lastScopeRef.current === null) {
    lastScopeRef.current = scopeSignature(values);
  }

  useFormikChangeListener<MoiAssignmentRuleFormValues>({
    listenForKeys: [FIELD_IDS.LOBS, FIELD_IDS.ALL_LOBS, FIELD_IDS.SUB_ORGANIZATION_IDS],
    onChange: (newValues) => {
      const signature = scopeSignature(newValues);
      if (signature === lastScopeRef.current) {
        return;
      }
      lastScopeRef.current = signature;
      setFieldValue(FIELD_IDS.CONDITIONS_JSON, null);
    },
    runOnFirstRender: false,
  });

  return (
    <CardDialog
      isDialog
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      title={isEditing ? TEXTS.DIALOG_TITLE_EDIT : TEXTS.DIALOG_TITLE_ADD}
    >
      <AlertBanner
        className="mb-4"
        alertType={AlertBanner.ALERT_TYPES.INFO}
        note={TEXTS.SCOPE_CHANGE_NOTE}
        title={TEXTS.SCOPE_CHANGE_NOTE_TITLE}
        withIcon
      />
      <TextFieldFormik
        id={FIELD_IDS.NAME}
        label={TEXTS.NAME_LABEL}
        className={cn(classes.textField, 'mb-20')}
        fullWidth
        disabled={isDisabled}
      />
      <div className="rounded-md border border-solid border-slate-600 px-20 pb-20 pt-16">
        <Heading variant={Heading.TYPES.H3} className="pb-12">
          {TEXTS.CONTEXT}
        </Heading>
        {subOrganizationEnabled ? (
          <div className="mb-12">
            <SubOrgMultiSelectWithChipsFormik
              id={FIELD_IDS.SUB_ORGANIZATION_IDS}
              label={TEXTS.SUB_ORGANIZATIONS_LABEL}
              showOnly={isSubmitting}
              disabled={isSubmitting}
            />
          </div>
        ) : null}
        <div className="mb-12">
          <LobMultiSelectWithChipsFormik
            lobsFieldId={FIELD_IDS.LOBS}
            allLobsFieldId={FIELD_IDS.ALL_LOBS}
            {...(subOrganizationEnabled
              ? {
                  subOrganizationIds: isAllSubOrganizationsSelected(subOrganizationIds)
                    ? subOrganizations.map((subOrg) => subOrg.id)
                    : subOrganizationIds,
                  subOrgFieldId: FIELD_IDS.SUB_ORGANIZATION_IDS,
                  shouldResetOnSubOrgChange: true,
                  disabled: isEmpty(subOrganizationIds),
                }
              : {})}
          />
        </div>
        <ArrayMultiselectFieldWithChipsFormik
          id={FIELD_IDS.MOI_METHODS}
          label={TEXTS.MOI_METHODS_LABEL}
          options={toMoiMethodOptions(methods)}
          allOption={ALL_MOI_METHODS_OPTION}
          showOnly={isSubmitting}
          disabled={isSubmitting}
        />
      </div>
      <div className="mt-20">
        <Heading variant={Heading.TYPES.H3} className="pb-12">
          {TEXTS.ASSIGNEE}
        </Heading>
        <MoiAssigneeFieldsFormik organizationId={organization.id} disabled={isSubmitting} />
      </div>
      <div className="px-4 py-20">
        <MoiAssignmentConditionsQueryBuilderFormik
          fieldId={FIELD_IDS.CONDITIONS_JSON}
          // Every picked LOB: the catalog is the cross-LOB intersection, which is also what
          // save-time validation checks the rule against.
          lobs={catalogLobs}
          isAllLobs={isAllLobs}
          isAllSubOrgs={subOrganizationEnabled ? isAllSubOrganizationsSelected(subOrganizationIds) : true}
          subOrgIds={subOrganizationEnabled ? subOrganizationIds : []}
          onLoadingChange={setIsLoadingConditions}
        />
      </div>
      <ButtonsContainer>
        <CancelButton onClick={onCancel} disabled={isSubmitting} />
        {/* Held while the methods load: the stale-method check needs them. */}
        <Button color="primary" onClick={() => handleSubmit()} disabled={isDisabled || isLoadingMethods}>
          {isEditing ? TEXTS.UPDATE : TEXTS.SAVE}
        </Button>
      </ButtonsContainer>
    </CardDialog>
  );
};

export default AddOrEditMoiAssignmentRuleDialog;
// The inner component is exported for testing the Formik wiring (the All-methods selection and the
// scope-change reset of the conditions tree). Production code should use the default export.
export { AddOrEditMoiAssignmentRuleDialogInner };
